import React, {FC} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button
} from '@mui/material';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {getCmsData} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import {LoadingButton} from '@mui/lab';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';

interface AddEmbeddedCodeDialogProps {
  token: string | undefined;
  parentId: string | undefined;
  setTreeData?: (callback: (prevTreeData: any[]) => any[]) => void;
  data?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
}

interface FormInputs {
  nametyped: string;
  embeddedCode: string;
}

const AddEmbeddedCodeDialog: FC<AddEmbeddedCodeDialogProps> = ({
  token,
  parentId,
  setTreeData,
  open,
  onCancel,
  onClose
}) => {
  // Define Yup schema for validation
  const schema = Yup.object().shape({
    nametyped: Yup.string()
      .required('Name is required')
      .min(3, 'Name must be at least 3 characters long'),
    embeddedCode: Yup.string()
      .required('Embedded Code is required')
      .min(10, 'Embedded Code must be at least 10 characters long')
  });
  window.addEventListener('keydown', event => {
    // Ignore key events if the target is an input, textarea, or contenteditable element
    const target = event.target as HTMLElement;

    // Check if the target is an input, textarea, or contenteditable element
    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    if (isInputField) {
      return; // Exit if the focus is on an input field
    }

    // Handle other global key events here
    if (event.key === 'a' || event.key === 'd' || event.key === 'r') {
      console.log(`Key pressed: ${event.key}`);
      // Add your custom logic for these keys
    }
  });
  const {
    register,
    handleSubmit,
    formState: {errors, isSubmitting}
  } = useForm<FormInputs>({
    resolver: yupResolver(schema)
  });

  // Handle form submission
  const onSubmit = async (data: FormInputs) => {
    try {
      const newChildNode = {
        Name: data.nametyped,
        ParentId: parentId,
        NodeType: 'Embedded code',
        Icon: 'embedded_icon',
        Access: true,
        MimeType: 'embeddedcode',
        UploadedFile: data.embeddedCode,
        children: []
      };

      const requestData: any = {
        sql_data: token,
        data: newChildNode,
        operation: 'INSERT'
      };
      const encryptRequestData = EncryptUtils.encryptedData(requestData);
      const response = await getCmsData('LMS', 'content-management', {
        tk: encryptRequestData
      });

      const {Name, Id} = response?.data[0] || {};

      // Update tree data
      setTreeData?.((prevTreeData: any[]) => {
        const addNodeToParent = (nodes: any[]): any[] =>
          nodes.map(node => {
            if (node.Id === parentId) {
              return {
                ...node,
                hasDocuments: true,
                children: [
                  ...(node.children || []),
                  {
                    Id: Id,
                    Name: Name,
                    ParentId: parentId,
                    NodeType: 'Embedded code',
                    Icon: 'embedded_icon',
                    Access: true,
                    MimeType: 'embeddedcode',
                    UploadedFile: data.embeddedCode,
                    children: []
                  }
                ]
              };
            }
            if (node.children) {
              return {
                ...node,
                children: addNodeToParent(node.children)
              };
            }
            return node;
          });

        return addNodeToParent(prevTreeData);
      });

      // Close the dialog
      onClose();

      enqueueSnackbar(
        'The Embedded Code is Added',
        SnackBarConfig.getSuccess()
      );
    } catch (error) {
      console.error('Failed to add embedded code:', error);
      onClose();
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Add Embedded Code</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('nametyped')}
            label="Name"
            fullWidth
            margin="dense"
            variant="outlined"
            onKeyDown={event => {
              event.stopPropagation(); // Prevent the event from bubbling to global handlers
            }}
            error={!!errors.nametyped}
            helperText={errors.nametyped?.message}
          />
          <TextField
            {...register('embeddedCode')}
            label="Embedded Code"
            fullWidth
            margin="dense"
            variant="outlined"
            onKeyDown={event => {
              event.stopPropagation(); // Prevent the event from bubbling to global handlers
            }}
            multiline
            error={!!errors.embeddedCode}
            helperText={errors.embeddedCode?.message}
          />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onCancel}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="success"
              onClick={handleSubmit(onSubmit)} // Correctly invoke handleSubmit
              loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmbeddedCodeDialog;
